import { EApplicantType, EFinancingProgram } from '@if/core/types'
import LaunchIcon from '@mui/icons-material/Launch'
import { Box, Grid, IconButton, useTheme } from '@mui/material'
import Typography from '@mui/material/Typography'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { FraudFlagDto } from '@src/data/api/fraud/fraud-api'
import { ROUTES } from '@src/data/routes'
import { useGetFraudAnalysis, useGetIPQSAnalysis } from '@src/data/selectors/credit-selectors'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  activeFlags: FraudFlagDto[] | null | undefined
  applicantType: EApplicantType
  size: string | null
}

const FraudWarningMessageList = ({ activeFlags, applicantType, size }: Props) => {
  const theme = useTheme()
  const borderColor = theme.palette.divider
  const { t, i18n } = useTranslation()

  const manualFlags = activeFlags?.filter((f) => !f.isAutomatic) || []
  const autoFlags = activeFlags?.filter((f) => f.isAutomatic) || []
  const fraudAnalysis = useGetFraudAnalysis()
  const ipqsAnalysis = useGetIPQSAnalysis(applicantType)

  const { creditApplication } = useCreditApplicationData()
  const hideIpqsFraudIq =
    creditApplication?.financingProgramId === EFinancingProgram.IfXpress ||
    creditApplication?.financingProgramId === EFinancingProgram.MechanicalWarranty

  const launchFraudFlagsPage = useCallback(() => {
    if (creditApplication?.id !== undefined && creditApplication?.financingProgramId !== undefined)
      window.open(ROUTES.applications.fraudFlags(creditApplication.financingProgramId, creditApplication.id), '_blank')
  }, [creditApplication?.id, creditApplication?.financingProgramId])

  return (
    <Grid
      size={{
        xs: 12,
        md: 12,
        lg: size === 'half' ? 6 : 12,
      }}
    >
      <Box
        sx={{
          border: '1px solid',
          borderColor,
          borderRadius: 2,
          p: 1,
        }}
      >
        <Typography>{t('fraudFlags.label')}
          <IconButton aria-label="launch" color='primary' size="small" onClick={launchFraudFlagsPage}>
            <LaunchIcon />
          </IconButton>
        </Typography>
        <ul>
          {!hideIpqsFraudIq && fraudAnalysis?.fraudIQAnalysis?.decision && fraudAnalysis.fraudIQAnalysis.decision !== 'CLEAR' && (
            <li>{t('warningMessages.FraudIQNotClearWarning', 'FraudIQ analysis result is not clear')}</li>
          )}
          {!hideIpqsFraudIq && (ipqsAnalysis?.emailFraudScore ?? 0) > 0 && (
            <li>{t('fraudFlags.scores.ipqsEmail', 'IPQS Email Score')}: {ipqsAnalysis?.emailFraudScore}</li>
          )}
          {!hideIpqsFraudIq && (ipqsAnalysis?.phoneFraudScore ?? 0) > 0 && (
            <li>{t('fraudFlags.scores.ipqsPhone', 'IPQS Phone Score')}: {ipqsAnalysis?.phoneFraudScore}</li>
          )}
          {manualFlags.length > 0 && (
            <li>{t('fraudFlags.manual')} {t('fraudFlags.flags')}: {manualFlags?.map((x) => (i18n.language === 'fr' ? x.displayNameFr : x.displayNameEn).trim()).join(' / ') || t('fraudFlags.noActiveFlags')}</li>
          )}
          {autoFlags.length > 0 && (
            <li>{t('fraudFlags.automatic')} {t('fraudFlags.flags')} : {autoFlags?.map((x) => (i18n.language === 'fr' ? x.displayNameFr : x.displayNameEn).trim()).join(' / ') || t('fraudFlags.noActiveFlags')}</li>
          )}
        </ul>
      </Box>
    </Grid>
  )
}

export default FraudWarningMessageList
